import React, { useState, useEffect } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Switch,
  Avatar,
  IconButton,
  Typography,
  Grid,
  Button,
  TextField,
  Radio,
  Checkbox,
  Slider,
} from "@mui/material";
import {
  Home as HomeIcon,
  Settings as SettingsIcon,
  RocketLaunch as RocketLaunchIcon,
  AutoStories as AutoStoriesIcon,
  MonetizationOn as MonetizationOnIcon,
  Science as ScienceIcon,
  Inventory as InventoryIcon,
  DeviceHub as FlowIcon,
} from "@mui/icons-material";
import WordLogo from "../../assets/icons/BunyanLogo";
import WordLogoDark from "../../assets/icons/BunyanLogoDark";
import BunyanTextLogo from "../../assets/icons/BunyanTextLogo";
import BunyanTextLogoRTL from "../../assets/icons/BunyanTextLogoRTL";
import BunyanTextLogoDark from "../../assets/icons/BunyanTextLogoDark";
import BunyanTextLogoDarkRTL from "../../assets/icons/BunyanTextLogoDarkRTL";
import { useAuth } from "../../contexts/AuthContext";
import { styled, alpha } from "@mui/material/styles";
import { getContrastColor } from "@/utils/theme-context";
import { useTranslation } from "react-i18next";

const MaterialUISwitch = styled(Switch)(({ theme, ...ownerState }) => {
  const { themeobj, mode } = ownerState || {};
  if (!themeobj) return {};

  const bg = themeobj.backgroundColor?.[mode];
  const isRTL = theme.direction === "rtl";

  return {
    width: 62,
    height: 34,
    padding: 7,
    "& .MuiSwitch-switchBase": {
      margin: 1,
      padding: 0,
      transform: isRTL
        ? "translateX(-6px) !important"
        : "translateX(6px) !important",
      "&.Mui-checked": {
        color: "var(--color-3)",
        transform: isRTL
          ? "translateX(-22px) !important"
          : "translateX(22px) !important",
        "& .MuiSwitch-thumb:before": {
          backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
            "var(--color-3)",
          )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
        },
        "& + .MuiSwitch-track": {
          backgroundColor: bg,
          opacity: 0.5,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      backgroundColor: bg,
      width: 32,
      height: 32,
      "&:before": {
        content: "''",
        position: "absolute",
        width: "100%",
        height: "100%",
        insetInlineStart: 0,
        top: 0,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "var(--color-3)",
        )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
      },
    },
    "& .MuiSwitch-track": {
      opacity: 0.3,
      borderRadius: 20 / 2,
    },
  };
});

const PreviewButton = styled(Button)(({ theme, ...ownerState }) => {
  const { themeobj, variantkey, mode } = ownerState || {};
  if (!themeobj) return {};

  const v = themeobj[variantkey];
  if (!v) return {};

  const bg = v.backgroundColor?.[mode];
  const hoverBg = v.hoverBackgroundColor?.[mode];
  const text = v.textColor?.[mode];
  const hoverText = v.hoverTextColor?.[mode];
  const border = v.borderColor?.[mode];
  const hoverBorder = v.hoverBorderColor?.[mode];

  return {
    transition: "all 0.18s ease",

    // contained
    "&.MuiButton-contained": {
      color: text,
      backgroundColor: bg,
      border: `1px solid ${border}`,
      boxShadow: "none",
      "&:hover": {
        backgroundColor: hoverBg,
        color: hoverText,
        border: `1px solid ${hoverBorder}`,
        boxShadow: "none",
      },
    },

    // outlined
    "&.MuiButton-outlined": {
      color: bg,
      backgroundColor: "transparent",
      border: `1px solid ${border}`,
      "&:hover": {
        color: hoverText,
        border: `1px solid ${hoverBorder}`,
        backgroundColor: hoverBg,
      },
    },

    // text
    "&.MuiButton-text": {
      color: bg,
      backgroundColor: "transparent",
      border: "none",
      "&:hover": {
        color: hoverText,
        backgroundColor: hoverBg,
      },
    },
  };
});

const PreviewTextField = styled(TextField)(({ theme, ...ownerState }) => {
  const { themeobj, mode } = ownerState || {};
  if (!themeobj) return {};

  const bg = themeobj.backgroundColor?.[mode];
  const text = themeobj.textColor?.[mode];
  const border = themeobj.borderColor?.[mode];
  const br = themeobj.borderRadius?.[mode];

  return {
    // Filled variant styles
    "& .MuiFilledInput-root": {
      color: text,
      backgroundColor: bg,
      borderRadius: `${br} ${br} 0 0`,
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: border,
      },
      "&:after": {
        borderBottomColor: border,
        borderBottomWidth: "2px",
      },
      "&.Mui-focused": {
        backgroundColor: bg,
        "&:after": {
          borderBottomColor: border,
        },
      },
      "& input": {
        color: text,
        caretColor: text,
      },
      "& textarea": {
        color: text,
        caretColor: text,
      },
    },
    // Outlined variant styles
    "& .MuiOutlinedInput-root": {
      color: text,
      backgroundColor: bg,
      borderRadius: br,
      "&:hover fieldset": {
        borderColor: border,
      },
      "&.Mui-focused fieldset": {
        borderColor: border,
        borderWidth: "2px",
      },
      "& input": {
        color: text,
        caretColor: text,
      },
      "& textarea": {
        color: text,
        caretColor: text,
      },
    },
    // Standard variant styles
    "& .MuiInput-root": {
      color: text,
      backgroundColor: "transparent",
      borderRadius: br,
      "&:hover:not(.Mui-disabled):before": {
        borderBottomColor: border,
      },
      "&:after": {
        borderBottomColor: border,
      },
      "&.Mui-focused fieldset": {
        borderColor: border,
        borderWidth: "2px",
      },
      "& input": {
        color: text,
        caretColor: text,
      },
      "& textarea": {
        color: text,
        caretColor: text,
      },
    },
    "& .MuiInputLabel-root": {
      color: text,
    },
  };
});

const PreviewRadio = styled(Radio)(({ theme, ...ownerState }) => {
  const { themeobj, mode } = ownerState || {};
  if (!themeobj) return {};

  const bg = themeobj.backgroundColor?.[mode];

  return {
    color: bg,
    "&.Mui-checked": {
      color: bg,
    },
    "&.Mui-checked .MuiSvgIcon-root": {
      color: bg,
    },
  };
});

const PreviewCheckbox = styled(Checkbox)(({ theme, ...ownerState }) => {
  const { themeobj, mode } = ownerState || {};
  if (!themeobj) return {};

  const bg = themeobj.backgroundColor?.[mode];

  return {
    color: bg,
    "&.Mui-checked": {
      color: bg,
    },
    "&.Mui-checked .MuiSvgIcon-root": {
      color: bg,
    },
  };
});

const PreviewSwitch = styled(Switch)(({ theme, ...ownerState }) => {
  const { themeobj, mode } = ownerState || {};
  if (!themeobj) return {};

  const bg = themeobj.backgroundColor?.[mode];

  return {
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: bg,
      "& + .MuiSwitch-track": {
        backgroundColor: bg,
        opacity: 0.5,
      },
    },
    "& .MuiSwitch-switchBase.Mui-checked:hover": {
      backgroundColor: "rgba(0,0,0,0.08)",
    },
  };
});

const PreviewSlider = styled(Slider)(({ theme, ...ownerState }) => {
  const { themeobj, mode } = ownerState || {};
  if (!themeobj) return {};

  const bg = themeobj.backgroundColor?.[mode];

  return {
    color: bg,
    "& .MuiSlider-thumb": {
      backgroundColor: bg,
      border: `2px solid ${bg}`,
      "&:hover": {
        boxShadow: "0 0 0 6px rgba(0,0,0,0.1)",
      },
    },
    "& .MuiSlider-track": {
      backgroundColor: bg,
    },
    "& .MuiSlider-rail": {
      opacity: 0.3,
      backgroundColor: bg,
    },
    "& .MuiSlider-valueLabel": {
      backgroundColor: bg,
    },
  };
});

const SubLayout = ({ theme = {}, mode = "light" }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const { currentUser, userProfile } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(true);

  const menuItems = [
    { text: t("sidebar.dashboard"), icon: <HomeIcon /> },
    // { text: t("sidebar.deployments"),, icon: <RocketLaunchIcon /> },
    { text: t("sidebar.documentCollections"), icon: <AutoStoriesIcon /> },
    { text: t("sidebar.aiCatalog"), icon: <InventoryIcon /> },
    { text: t("sidebar.flows"), icon: <FlowIcon /> },
    { text: t("sidebar.playground"), icon: <ScienceIcon /> },
    { text: t("sidebar.settings"), icon: <SettingsIcon /> },
    { text: t("sidebar.usageCost"), icon: <MonetizationOnIcon /> },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      {/* Sidebar */}
      <Box
        onMouseEnter={() => setIsCollapsed(false)}
        onMouseLeave={() => setIsCollapsed(true)}
        sx={{
          width: isCollapsed ? "105px" : "500px",
          backgroundColor: theme?.sidebar?.backgroundColor[mode],
          color: theme?.sidebar?.textColor[mode],
          transition: "width 0.3s ease",
          overflow: "hidden",
          whiteSpace: "nowrap",
          "&:hover": {
            "& .word-logo": {
              display: "none",
            },
            "& .text-logo": {
              display: "block",
            },
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            padding: "18px 10px",
          }}
        >
          <Box
            sx={{
              width: "45px",
              transition: "opacity 0.3s ease",
            }}
            className="word-logo"
          >
            {theme.logo[mode] ? (
              <img
                src={theme.logo[mode]}
                alt="logo"
                crossOrigin="anonymous"
                style={{
                  height: "40px",
                  width: "50px",
                  objectFit: "contain",
                }}
              />
            ) : (
              <WordLogo height={40} width={50} />
            )}
          </Box>
          <Box
            className="text-logo"
            sx={{
              display: "none",
              transition: "opacity 0.3s ease",
            }}
          >
            {theme.wideLogo[mode] ? (
              <img
                src={theme.wideLogo[mode]}
                alt="wideLogo"
                crossOrigin="anonymous"
                style={{
                  height: "38px",
                  width: "auto",
                  maxWidth: "183px",
                  objectFit: "contain",
                  objectPosition: isRTL ? "right center" : "left center",
                  display: "block",
                }}
              />
            ) : mode === "light" ? (
              isRTL ? (
                <BunyanTextLogoRTL height={38} width={183} />
              ) : (
                <BunyanTextLogo height={38} width={183} />
              )
            ) : isRTL ? (
              <BunyanTextLogoDarkRTL height={38} width={183} />
            ) : (
              <BunyanTextLogoDark height={38} width={183} />
            )}
          </Box>
        </Box>
        <List sx={{ p: 0 }}>
          {menuItems.map((item, i) => (
            <Tooltip
              title={isCollapsed ? item.text : ""}
              placement="right"
              key={i}
            >
              <ListItemButton
                sx={{
                  px: 2.5,
                  minHeight: 46,
                  backgroundColor:
                    item.text === "Dashboard"
                      ? theme?.sidebar?.activeLinkBackgroundColor[mode]
                      : theme?.sidebar?.backgroundColor[mode],
                  transition: "background-color 0.25s ease",
                  "&:hover": {
                    backgroundColor: theme?.sidebar?.hoverBackgroundColor[mode],
                  },
                  "&:hover .MuiListItemIcon-root .MuiSvgIcon-root": {
                    color: theme?.sidebar?.hoverTextColor[mode],
                  },
                  "&:hover .MuiListItemText-root .MuiTypography-root": {
                    color: theme?.sidebar?.hoverTextColor[mode],
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    marginInlineEnd: isCollapsed ? 0 : 2,
                    justifyContent: "center",
                    "& .MuiSvgIcon-root": {
                      color:
                        item.text === "Dashboard"
                          ? theme?.sidebar?.activeLinkTextColor[mode] ||
                            "var(--color-8)"
                          : theme?.sidebar?.textColor[mode],
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      sx: {
                        color:
                          item.text === "Dashboard"
                            ? theme?.sidebar?.activeLinkTextColor[mode] ||
                              "var(--color-8)"
                            : theme?.sidebar?.textColor[mode],
                      },
                    }}
                    sx={{
                      minWidth: 0,
                    }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          ))}
        </List>
      </Box>

      {/* Main Section */}
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            backgroundColor: theme?.header?.backgroundColor[mode],
            color: theme?.header?.textColor[mode],
            padding: "0.75rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {t("uiCustomization.subLayout.pageTitle")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              pointerEvents: "none",
            }}
          >
            {/* <Tooltip
              title={
                mode === "dark" ? "Switch to Light Mode" : "Switch to Dark Mode"
              }
            >
              <MaterialUISwitch
                checked={mode === "dark"}
                themeobj={theme.inputSwitch}
                mode={mode}
              />
            </Tooltip> */}
            <Tooltip title="Account">
              <IconButton
                size="small"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                color="inherit"
              >
                {userProfile?.photoURL ? (
                  <Avatar
                    src={userProfile.photoURL}
                    alt={userProfile.displayName || currentUser.email}
                  />
                ) : (
                  <Avatar
                    sx={{
                      bgcolor: theme?.header?.iconColor[mode],
                      color: getContrastColor(
                        theme?.header?.iconColor[mode] || "var(--color-8)",
                      ),
                    }}
                  >
                    {(userProfile?.displayName || currentUser?.email || "")
                      .charAt(0)
                      .toUpperCase()}
                  </Avatar>
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box
          sx={{
            backgroundColor: theme?.general?.backgroundColor[mode],
            color: theme?.general?.textColor[mode],
            flexGrow: 1,
            padding: "1rem",
          }}
        >
          <Box
            sx={{
              border: "1px solid",
              padding: "1rem",
              maxHeight: "40rem",
              overflow: "auto",
              borderRadius: theme?.card?.borderRadius[mode],
              borderColor: theme?.card?.borderColor[mode],
              background: theme?.card?.backgroundColor[mode],
              color: theme?.general?.textColor[mode],
            }}
          >
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: "1rem", fontWeight: "600" }}>
                {t("uiCustomization.subLayout.cardTitle")}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: "400",
                }}
              >
                {t("uiCustomization.subLayout.cardDescription")}
              </Typography>
            </Box>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.primaryButton")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="primary"
                      mode={mode}
                      variant="contained"
                    >
                      {t("uiCustomization.subLayout.contained")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="primary"
                      mode={mode}
                      variant="outlined"
                    >
                      {t("uiCustomization.subLayout.outlined")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="primary"
                      mode={mode}
                      variant="text"
                    >
                      {t("uiCustomization.subLayout.text")}
                    </PreviewButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.secondaryButton")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="secondary"
                      mode={mode}
                      variant="contained"
                    >
                      {t("uiCustomization.subLayout.contained")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="secondary"
                      mode={mode}
                      variant="outlined"
                    >
                      {t("uiCustomization.subLayout.outlined")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="secondary"
                      mode={mode}
                      variant="text"
                    >
                      {t("uiCustomization.subLayout.text")}
                    </PreviewButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.successButton")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="success"
                      mode={mode}
                      variant="contained"
                    >
                      {t("uiCustomization.subLayout.contained")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="success"
                      mode={mode}
                      variant="outlined"
                    >
                      {t("uiCustomization.subLayout.outlined")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="success"
                      mode={mode}
                      variant="text"
                    >
                      {t("uiCustomization.subLayout.text")}
                    </PreviewButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.errorButton")}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="error"
                      mode={mode}
                      variant="contained"
                    >
                      {t("uiCustomization.subLayout.contained")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="error"
                      mode={mode}
                      variant="outlined"
                    >
                      {t("uiCustomization.subLayout.outlined")}
                    </PreviewButton>
                  </Grid>
                  <Grid item>
                    <PreviewButton
                      size="small"
                      themeobj={theme.button}
                      variantkey="error"
                      mode={mode}
                      variant="text"
                    >
                      {t("uiCustomization.subLayout.text")}
                    </PreviewButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={4} sx={{ mb: 3 }}>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.input")}
                </Typography>
                <Grid container spacing={4} alignItems="center">
                  <Grid item>
                    <PreviewTextField
                      themeobj={theme.input}
                      mode={mode}
                      id="outlined-basic"
                      label={t("uiCustomization.subLayout.outlined")}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item>
                    <PreviewTextField
                      themeobj={theme.input}
                      mode={mode}
                      id="filled-basic"
                      label={t("uiCustomization.subLayout.filled")}
                      variant="filled"
                    />
                  </Grid>
                  <Grid item>
                    <PreviewTextField
                      themeobj={theme.input}
                      mode={mode}
                      id="standard-basic"
                      label={t("uiCustomization.subLayout.standard")}
                      variant="standard"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={4} sx={{ mb: 3 }}>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.radiobutton")}
                </Typography>
                <PreviewRadio
                  themeobj={theme.radiobutton}
                  mode={mode}
                  checked={true}
                />
              </Grid>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.checkbox")}
                </Typography>
                <PreviewCheckbox
                  themeobj={theme.checkBox}
                  mode={mode}
                  defaultChecked
                />
              </Grid>
              <Grid item>
                <Typography
                  sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}
                >
                  {t("uiCustomization.formBuilder.switch")}
                </Typography>
                <PreviewSwitch
                  themeobj={theme.inputSwitch}
                  mode={mode}
                  defaultChecked
                />
              </Grid>
            </Grid>
            <Box>
              <Typography sx={{ fontSize: "1rem", fontWeight: "600", mb: 0.5 }}>
                {t("uiCustomization.formBuilder.slider")}
              </Typography>
              <PreviewSlider
                themeobj={theme.slider}
                mode={mode}
                defaultValue={30}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SubLayout;
